import { useEffect, useMemo } from 'react';
import clsx from 'clsx';
import type { GroupMember } from '@flock-types/chat.types';
import Avatar from '@/components/Avatar';

interface Props {
  members: GroupMember[];
  searchQuery: string;
  selectedIndex: number;
  onSelect: (member: GroupMember) => void;
  onSelectedIndexChange: (index: number) => void;
  /** Reports the filtered list so the composer can clamp keyboard navigation. */
  onFilteredChange: (filtered: GroupMember[]) => void;
}

/** Dropdown for @mention autocomplete (port of mobile MentionAutocomplete). */
export default function MentionAutocomplete({
  members,
  searchQuery,
  selectedIndex,
  onSelect,
  onSelectedIndexChange,
  onFilteredChange,
}: Props) {
  const filtered = useMemo(() => {
    if (!searchQuery) return members.slice(0, 6);
    return members
      .filter((m) => m.userName.toLowerCase().includes(searchQuery.toLowerCase()))
      .slice(0, 6);
  }, [members, searchQuery]);

  useEffect(() => {
    onFilteredChange(filtered);
  }, [filtered, onFilteredChange]);

  if (filtered.length === 0) return null;

  return (
    <div className="absolute bottom-full left-0 z-20 mb-1 w-72 overflow-hidden rounded-xl border border-flock-gray-200 bg-white shadow-xl">
      <div className="px-3 py-1.5 text-xs font-medium text-flock-navy-400">Mention someone</div>
      {filtered.map((member, index) => (
        <button
          key={member.userId}
          onMouseEnter={() => onSelectedIndexChange(index)}
          onMouseDown={(e) => {
            // mousedown (not click) so the textarea doesn't lose focus first
            e.preventDefault();
            onSelect(member);
          }}
          className={clsx(
            'flex w-full items-center gap-3 px-3 py-2 text-left',
            index === selectedIndex ? 'bg-flock-navy-50' : ''
          )}
        >
          <Avatar src={member.profileImageUrl} name={member.userName} sizeClass="h-7 w-7" textClass="text-[10px]" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-flock-navy-900">{member.userName}</div>
            <div className="text-xs capitalize text-flock-navy-400">{member.role}</div>
          </div>
        </button>
      ))}
    </div>
  );
}
